/**
 * Function naming convention: api{FunctionName}
 * File naming convention: {FunctionName}.ts
 */

interface AbortControllers {
  create: (key: string) => AbortController;
  cancel: (key: string) => void;
  cancelAll: () => void;
}

/**
 * Helper function to create a map of AbortControllers and use them to cancel fetch requests.
 *
 * @example
 * const abortControllers = abortControllers();
 * const controller = abortControllers.create("key");
 * controller.abort();
 * abortControllers.cancel("key");
 * abortControllers.cancelAll();
 *
 * @returns {AbortControllers}
 */
function abortControllers(): AbortControllers {
  const abortControllers = new Map<string, AbortController>();

  const isAborted = (controller: AbortController) => controller.signal.aborted;

  const cancel: AbortControllers["cancel"] = (key) => {
    const controller = abortControllers.get(key);

    if (controller instanceof AbortController && !isAborted(controller)) {
      controller.abort();
      abortControllers.delete(key);
    }
  };

  const create: AbortControllers["create"] = (key) => {
    cancel(key);
    const controller = new AbortController();
    abortControllers.set(key, controller);

    return controller;
  };

  const cancelAll: AbortControllers["cancelAll"] = () => {
    for (const controller of abortControllers.values()) {
      if (controller instanceof AbortController && !isAborted(controller)) {
        controller.abort();
      }
    }

    abortControllers.clear();
  };

  return {
    create,
    cancel,
    cancelAll,
  };
}

export const apiAbortControllers = abortControllers();
